import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form } from 'antd';
import styled from 'styled-components';

import { categories } from '@/entities/privateDocuments/components/PrivateDocumentSelectCategory';
import { useI18nValues } from '@/entities/privateDocuments/hooks/useI18nValues';
import { useNotices } from '@/features/notices/useNotices';
import { Country } from '@/shared/components';
import { CountriesList } from '@/shared/dictionaries/countries';
import { PrivateDocumentCategory, PrivateDocumentDto } from '@/shared/lib/sj-orm';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { Title } from '@/shared/uikit/Title';
import { Input } from '@/shared/uikitV2/Input';
import { Select } from '@/shared/uikitV2/Select';
import { Option } from '@/shared/uikitV2/Select/types';
import { FileUploader, FileUploaderRef, UploadState } from '@/widgets/FileUploader';

import { SelectOptions } from '../../components/SelectItem/types';

type FormFields = {
  privateCategory: PrivateDocumentCategory;
  name: string;
  country: string;
  attachments?: string[];
};

type CreatePdProps = {
  formId: string;
  onChangeState: (uploading: boolean) => void;
  onCreate: (data: SelectOptions) => void;
};

export const CreatePd: FC<CreatePdProps> = ({ formId, onChangeState, onCreate }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'selectCollectionItem' });
  const { keyPair } = useKeyPairStore();
  const sjdb = useSJDatabase();
  const uploaderRef = useRef<FileUploaderRef>(null);
  const [uploadState, setUploadState] = useState<UploadState | null>();
  const notice = useNotices();
  const i18n = useI18nValues();

  const handleSubmit = async ({ ...data }: FormFields) => {
    if (!keyPair || !uploaderRef.current) {
      return;
    }

    const res = await uploaderRef.current.upload?.(true);

    const pdDto: PrivateDocumentDto = {
      id: sjdb.generateId(CollectionName.PRIVATE_DOCUMENTS),
      beneficiaryId: 'me',
      type: DocumentType.PRIVATE_DOCUMENTS,
      ...data,
    };

    if (res.success) {
      pdDto.attachments = res.filePaths;
    }

    const pid = await sjdb.collections.privateDocuments?.create(pdDto, [keyPair.publicKey]);

    if (pid === undefined) {
      notice.error('Error during creating personal document!');
      return;
    }

    onCreate({
      colName: CollectionName.PRIVATE_DOCUMENTS,
      id: pid.id,
    });
  };

  const { control, handleSubmit: formHandleSubmit } = useForm<FormFields>();

  const categoryOptions: Array<Option> = useMemo(
    () =>
      categories.map((category) => ({
        content: { label: i18n.category(category) },
        value: category,
      })),
    [],
  );

  const countryOptions: Array<Option> = useMemo(
    () =>
      CountriesList.map((country) => ({
        content: { label: country.label },
        value: country.key,
      })),
    [],
  );

  useEffect(() => {
    onChangeState(Boolean(uploadState?.uploading));
  }, [uploadState?.uploading]);

  return (
    <Container>
      <Form style={{ display: 'contents' }} id={formId} onFinish={formHandleSubmit(handleSubmit)}>
        <Controller
          name={'privateCategory'}
          control={control}
          rules={{ required: t('mandatoryField') }}
          render={({ field, fieldState }) => (
            <Select
              required
              label={t('pds.type')}
              status={fieldState.error && 'error'}
              errorText={fieldState.error?.message}
              name={field.name}
              options={categoryOptions}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="name"
          control={control}
          rules={{ required: t('mandatoryField') }}
          render={({ field, fieldState }) => (
            <Input
              required
              label={t('pds.title')}
              type="text"
              status={fieldState.error ? 'error' : 'default'}
              errorText={fieldState.error?.message}
              {...field}
            />
          )}
        />
        <Controller
          name={'country'}
          control={control}
          rules={{ required: t('mandatoryField') }}
          render={({ field, fieldState }) => (
            <Select
              required
              iconAfter={field.value ? <Country icon code={field.value} /> : null}
              label={t('pds.country')}
              status={fieldState.error && 'error'}
              errorText={fieldState.error?.message}
              name={field.name}
              options={countryOptions}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Form>

      <Title size={16} bold>
        {t('filesTitle')}
      </Title>
      <FileUploader onUpdate={setUploadState} ref={uploaderRef} />
    </Container>
  );
};

const Container = styled.div`
  display: grid;
  flex-direction: column;
  gap: 16px;
`;
